import { transportData } from '../components/TransportData';
import { transportDataGaza } from '../components/TransportDataGaza';
import accommodationList from './accommodationDetails';

// Every fact below comes from the data files, so updating TransportData.jsx,
// TransportDataGaza.jsx or accommodationDetails.jsx updates the chatbot too.

const ROUTES = [transportData, transportDataGaza];

// The list can contain empty slots from extra commas
const accommodations = accommodationList.filter(Boolean);

const DEFAULT_SUGGESTIONS = ['How do I book?', 'Prices', 'Travel dates', 'Accommodation'];

// Short names people use for stops
const STOP_ALIASES = {
  plk: 'polokwane',
  thoho: 'thohoyandou',
  makhado: 'louis',
  trichardt: 'louis'
};

/* ---------- text matching ---------- */

const clean = (text) =>
  text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

// Edit distance that counts swapped neighbouring letters as one typo
const typoDistance = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j += 1) d[0][j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }

  return d[a.length][b.length];
};

const hasPhrase = (text, phrase) => ` ${text} `.includes(` ${phrase} `);

// 2 per word for an exact match, 1 for a likely typo, 0 for no match
const scoreKeyword = (text, keyword) => {
  if (hasPhrase(text, keyword) || hasPhrase(text, `${keyword}s`)) {
    return keyword.split(' ').length * 2;
  }

  if (keyword.includes(' ') || keyword.length < 5) return 0;

  const allowed = keyword.length >= 8 ? 2 : 1;
  const isTypo = text
    .split(' ')
    .some((word) => word.length >= 4 && typoDistance(word, keyword) <= allowed);

  return isTypo ? 1 : 0;
};

const scoreKeywords = (text, keywords) =>
  keywords.reduce((total, keyword) => total + scoreKeyword(text, keyword), 0);

/* ---------- reading the data ---------- */

const bullets = (items) => items.map((item) => `• ${item.trim()}`).join('\n');

const routesFor = (route) => (route ? [route] : ROUTES);

// Answers for one route, or every route when we don't know which one they mean
const perRoute = (route, describe) =>
  routesFor(route)
    .map((r) => `🚌 ${r.route}\n${describe(r)}`)
    .join('\n\n');

const stopName = (stop) => stop.replace(/\s*\d{1,2}:\d{2}\s*(am|pm)?/i, '').trim();

// Towns the bus serves, excluding campuses, which appear on more than one route
const townStops = (route) =>
  route === transportDataGaza ? route.pickupPoints : route.dropoffLocations;

const findStop = (text) => {
  const words = text.split(' ').map((word) => STOP_ALIASES[word] || word);
  const searchText = words.join(' ');

  for (const route of ROUTES) {
    for (const stop of townStops(route)) {
      const firstWord = clean(stopName(stop)).split(' ')[0];
      if (scoreKeyword(searchText, firstWord) > 0) return { route, stop };
    }
  }

  return null;
};

const detectRoute = (text) => {
  const stop = findStop(text);
  if (scoreKeyword(text, 'gaza') > 0) return transportDataGaza;
  if (scoreKeyword(text, 'venda') > 0) return transportData;
  return stop ? stop.route : null;
};

const stopPrice = (route, stop) =>
  route.pricing.dropoffPrices?.[stopName(stop)] || route.pricing.totalPrice;

const fareLines = (route) =>
  [
    `Fare: R${route.pricing.totalPrice}`,
    ...Object.entries(route.pricing.dropoffPrices || {}).map(
      ([town, price]) => `${town}: R${price}`
    ),
    `Deposit: R${route.pricing.deposit}`
  ].join('\n');

const tidy = (text) => text.replace(/nfsas/gi, 'NSFAS').replace(/\s+/g, ' ').trim();

const residenceSummary = (place) =>
  `• ${tidy(place.name)}\n  ${tidy(place.price)} · ${tidy(place.distance)}`;

const findResidence = (text) =>
  accommodations.find((place) => {
    const name = clean(place.name)
      .replace(/\b(student|accomodation|accommodation|residence)\b/g, ' ')
      .trim();
    return hasPhrase(text, name.split(/\s+/).slice(0, 2).join(' '));
  });

const CAMPUSES = [
  { label: 'VUT', keywords: ['vut'], pattern: /vut/i },
  { label: 'NWU', keywords: ['nwu', 'north west'], pattern: /nwu|north west/i },
  { label: 'Sedibeng', keywords: ['sedibeng'], pattern: /sedibeng/i }
];

/* ---------- buttons ---------- */

const whatsappAction = (route, label = 'Chat on WhatsApp', message = 'Hi V2V, I have a question.') => ({
  type: 'link',
  label,
  href: `https://wa.me/${(route || transportData).contact.whatsapp}?text=${encodeURIComponent(message)}`
});

const BOOK = { type: 'book', label: 'Open Booking Form' };
const ACCOMMODATION = { type: 'accommodation', label: 'View Accommodation' };

/* ---------- answers ---------- */

const routeOverview = (route) => ({
  text: `🚌 ${route.route}

📍 Pickup points:
${bullets(route.pickupPoints)}

📍 Drop-off locations:
${bullets(route.dropoffLocations)}

📅 Travel dates:
${bullets(route.travelDates)}

💰 ${fareLines(route)}`,
  actions: [BOOK],
  suggestions: ['How do I book?', 'Banking details', 'Vehicles']
});

const stopAnswer = ({ route, stop }) => ({
  text: `✅ Yes, ${stopName(stop)} is on our ${route.route} route.

Fare: R${stopPrice(route, stop)}
Deposit: R${route.pricing.deposit}`,
  actions: [BOOK],
  suggestions: ['Travel dates', 'Pickup points', 'Banking details']
});

const residenceAnswer = (place) => ({
  text: `🏠 ${tidy(place.name)}

📍 ${tidy(place.location)}
💰 ${tidy(place.price)}
🚶 ${tidy(place.distance)}

${tidy(place.paymentType)}`,
  actions: [
    whatsappAction(
      { contact: { whatsapp: place.whatsappNumber } },
      'WhatsApp this residence',
      `Hi, I am interested in ${tidy(place.name)}.`
    ),
    ACCOMMODATION
  ],
  suggestions: ['NSFAS residences', 'Monthly rent', 'Accommodation']
});

const INTENTS = [
  {
    id: 'help',
    keywords: ['help', 'what can you do', 'menu', 'options'],
    answer: () => ({
      text: `I can help you with:

• Booking a seat
• Routes, dates and departure times
• Pickup and drop-off points
• Prices, deposits and banking details
• Refunds and changing your date
• Song requests
• Student accommodation

Tap a button or type your question.`,
      suggestions: DEFAULT_SUGGESTIONS
    })
  },
  {
    id: 'book',
    keywords: ['book', 'bok', 'booking', 'seat', 'reserve', 'how do i book', 'get a seat', 'buy a ticket', 'ticket'],
    answer: (route) => ({
      text: `📝 How to book:

1. Open the Booking Form
2. Add your name and cellphone number
3. Choose your route, pickup point and travel date
4. Choose your drop-off location
5. Add a song request if you like (optional)
6. Submit to download your receipt
7. Pay your deposit and send your proof of payment on WhatsApp

Your booking is only confirmed once payment is received and verified.`,
      actions: [BOOK, whatsappAction(route)],
      suggestions: ['Prices', 'Banking details', 'Travel dates']
    })
  },
  {
    id: 'dates',
    keywords: ['date', 'when', 'which day', 'calendar', 'schedule', 'next trip', 'what day'],
    answer: (route) => ({
      text: `📅 Available travel dates:\n\n${perRoute(route, (r) => bullets(r.travelDates))}`,
      actions: [BOOK],
      suggestions: ['Pickup points', 'Prices', 'Is there space?']
    })
  },
  {
    id: 'times',
    keywords: ['time', 'departure', 'depart', 'leave', 'arrival', 'arrive', 'what time', 'how long'],
    answer: (route) => ({
      text: `⏰ Departure times are shown with each travel date:\n\n${perRoute(route, (r) => bullets(r.travelDates))}\n\nFor arrival times, please message us on WhatsApp.`,
      actions: [whatsappAction(route, 'Ask about arrival times')],
      suggestions: ['Pickup points', 'Drop-off locations']
    })
  },
  {
    id: 'pickup',
    keywords: ['pickup', 'pick up', 'boarding', 'board', 'collect', 'where do i wait'],
    answer: (route) => ({
      text: `📍 Pickup points:\n\n${perRoute(route, (r) => bullets(r.pickupPoints))}`,
      actions: [BOOK],
      suggestions: ['Drop-off locations', 'Travel dates']
    })
  },
  {
    id: 'dropoff',
    keywords: ['drop off', 'dropoff', 'destination', 'stops', 'where do you go', 'towns'],
    answer: (route) => ({
      text: `📍 Drop-off locations:\n\n${perRoute(route, (r) => bullets(r.dropoffLocations))}`,
      actions: [BOOK],
      suggestions: ['Prices', 'Travel dates']
    })
  },
  {
    id: 'johannesburg',
    keywords: ['jhb', 'joburg', 'johannesburg'],
    answer: () => ({
      text: `🚌 Please message us on WhatsApp for the latest Johannesburg trip details.`,
      actions: [whatsappAction(transportData, 'Ask about JHB trips')],
      suggestions: ['Routes', 'Travel dates']
    })
  },
  {
    id: 'routes',
    keywords: ['route', 'where do you travel', 'which routes', 'trips'],
    answer: () => ({
      text: `🚌 Our routes:\n\n${bullets(transportData.pickupTransport)}`,
      actions: [BOOK],
      suggestions: ['Travel dates', 'Pickup points', 'Prices']
    })
  },
  {
    id: 'deposit',
    keywords: ['deposit', 'balance', 'cash', 'pay on the day', 'remaining', 'pay the rest'],
    answer: (route) => ({
      text: `💵 Deposit:\n\n${perRoute(route, (r) => `R${r.pricing.deposit} secures your seat.`)}\n\nThe remaining balance can be paid in cash on the day of travel.`,
      suggestions: ['Banking details', 'Prices']
    })
  },
  {
    id: 'price',
    keywords: ['price', 'cost', 'fare', 'how much', 'fee', 'charge', 'amount'],
    answer: (route) => ({
      text: `💰 Prices:\n\n${perRoute(route, fareLines)}`,
      actions: [BOOK],
      suggestions: ['Banking details', 'Deposit', 'Travel dates']
    })
  },
  {
    id: 'banking',
    keywords: ['bank', 'banking', 'account', 'capitec', 'how do i pay', 'payment', 'pay', 'eft'],
    answer: (route) => ({
      text: `🏦 Banking details:\n\n${perRoute(
        route,
        (r) => `Bank: ${r.banking.bank}
Account Number: ${r.banking.accountNumber}
Account Owner: ${r.banking.accountOwner}
Reference: ${r.banking.reference}`
      )}\n\nPlease use the account for your route, then send your proof of payment on WhatsApp.`,
      actions: [whatsappAction(route, 'Send proof of payment', 'Hi V2V, here is my proof of payment.')],
      suggestions: ['Deposit', 'Refunds']
    })
  },
  {
    id: 'proof',
    keywords: ['proof', 'proof of payment', 'pop', 'i have paid', 'i paid'],
    answer: (route) => ({
      text: `🧾 Send your proof of payment to:\n\n${perRoute(
        route,
        (r) => `WhatsApp: ${r.contact.displayWhatsapp}\nEmail: ${r.contact.email}`
      )}\n\nYour booking is only confirmed once payment is received and verified.`,
      actions: [whatsappAction(route, 'Send proof of payment', 'Hi V2V, here is my proof of payment.')]
    })
  },
  {
    id: 'refund',
    keywords: ['refund', 'cancel', 'cancellation', 'money back', 'change date', 'change my date', 'reschedule', 'postpone', 'miss the bus'],
    answer: (route) => ({
      text: `📌 Refunds and date changes:

• Bookings are non-refundable
• You can only change your travel date up to 48 hours before you travel

To change your date, message us on WhatsApp.`,
      actions: [whatsappAction(route, 'Change my travel date', 'Hi V2V, I would like to change my travel date.')]
    })
  },
  {
    id: 'confirmed',
    keywords: ['confirmed', 'confirm', 'confirmation', 'is my booking', 'receipt', 'did it work'],
    answer: (route) => ({
      text: `✅ After you submit the Booking Form, your receipt downloads automatically. Please keep it for your records.

Your booking is only confirmed once payment is received and verified.`,
      actions: [whatsappAction(route, 'Check my booking', 'Hi V2V, please check my booking. My reference is: ')]
    })
  },
  {
    id: 'vehicles',
    keywords: ['vehicle', 'bus', 'taxi', 'fleet', 'seater', 'car', 'sprinter'],
    answer: () => ({
      text: `🚌 Available vehicles:\n\n${bullets(transportData.fleet)}`,
      suggestions: ['Travel dates', 'Prices']
    })
  },
  {
    id: 'space',
    keywords: ['seats left', 'still space', 'any space', 'is there space', 'is it full', 'sold out', 'seats available', 'available seats', 'still book'],
    answer: () => ({
      text: `✅ Space is still available if the Booking Form allows you to select a date.\n\nSeats are limited, so book early.`,
      actions: [BOOK]
    })
  },
  {
    id: 'recess',
    keywords: ['recess', 'holiday', 'break', 'teaching', 'practical', 'tp', 'school'],
    answer: (route) => ({
      text: `🎒 Recess and practicals:

We always provide transport during recess periods if there is sufficient demand.

We also provide transport for Teaching Practicals. Contact us with your dates.`,
      actions: [whatsappAction(route, 'Send us your dates')]
    })
  },
  {
    id: 'song',
    keywords: ['song', 'music', 'playlist', 'artist', 'dj'],
    answer: () => ({
      text: `🎵 You can add a song request when you book. Just fill in the song name and artist on the Booking Form, and we'll play the requests as we leave.\n\nIt's optional, so you can leave it empty.`,
      actions: [BOOK]
    })
  },
  {
    id: 'accommodationPrice',
    keywords: ['rent', 'per month', 'monthly', 'cheap', 'cheapest', 'budget'],
    answer: () => ({
      text: `💰 Residences with a listed monthly rent:\n\n${accommodations
        .filter((place) => /R\d/.test(place.price))
        .map(residenceSummary)
        .join('\n\n')}\n\nSee the Accommodation page for all our residences.`,
      actions: [ACCOMMODATION],
      suggestions: ['NSFAS residences', 'Near VUT', 'Near NWU']
    })
  },
  {
    id: 'nsfas',
    keywords: ['nsfas', 'nfsas', 'accredited', 'bursary'],
    answer: () => ({
      text: `🎓 NSFAS-accredited residences:\n\n${accommodations
        .filter((place) => /nsfas|nfsas/i.test(place.price))
        .map(residenceSummary)
        .join('\n\n')}`,
      actions: [ACCOMMODATION],
      suggestions: ['Near VUT', 'Near NWU', 'Monthly rent']
    })
  },
  {
    id: 'accommodation',
    keywords: ['accommodation', 'accomodation', 'residence', 'res', 'room', 'rooms', 'housing', 'near', 'place to stay', 'where to stay', 'stay'],
    answer: (route, text) => {
      const campus = CAMPUSES.find(({ keywords }) => scoreKeywords(text, keywords) > 0);
      const places = campus
        ? accommodations.filter((place) => campus.pattern.test(`${place.price} ${place.distance}`))
        : accommodations;

      return {
        text: `🏠 ${campus ? `Residences near ${campus.label}` : 'Our student residences'}:\n\n${places
          .map(residenceSummary)
          .join('\n\n')}\n\nAsk me about any residence by name for more details.`,
        actions: [ACCOMMODATION],
        suggestions: ['NSFAS residences', 'Monthly rent', 'Near VUT', 'Near NWU']
      };
    }
  },
  {
    id: 'about',
    keywords: ['about', 'who are you', 'what is v2v', 'v2v', 'safe', 'reliable', 'trust'],
    answer: () => ({
      text: `🚌 V2V provides safe, affordable transport for students, and helps you find student accommodation near campus.`,
      actions: [BOOK, ACCOMMODATION],
      suggestions: DEFAULT_SUGGESTIONS
    })
  },
  {
    id: 'contact',
    keywords: ['contact', 'whatsapp', 'email', 'call', 'phone', 'number', 'human', 'person', 'agent', 'admin', 'speak to', 'talk to'],
    answer: (route) => ({
      text: `📞 Contact us:\n\n${perRoute(
        route,
        (r) => `WhatsApp: ${r.contact.displayWhatsapp}\nEmail: ${r.contact.email}`
      )}`,
      actions: [
        whatsappAction(route),
        { type: 'link', label: 'Join our WhatsApp group', href: transportData.contact.whatsappGroup }
      ]
    })
  },
  {
    id: 'thanks',
    weak: true,
    keywords: ['thanks', 'thank you', 'thank', 'appreciate', 'bye', 'goodbye', 'ndo livhuwa'],
    answer: () => ({
      text: `You're welcome! 😊 Safe travels with V2V.`,
      suggestions: DEFAULT_SUGGESTIONS
    })
  },
  {
    id: 'greeting',
    weak: true,
    keywords: ['aa', 'ndaa', 'naa', 'vho twa hani', 'hi', 'hello', 'hey', 'howzit', 'morning', 'afternoon', 'evening'],
    answer: () => ({
      text: `Ndaa/Aa! 👋 Vha khou tuwa afho?\n\nWelcome to V2V Support. How can I help you today?`,
      suggestions: DEFAULT_SUGGESTIONS
    })
  }
];

const fallback = (route) => ({
  text: `I'm not sure about that, athi divhi na nne. Please text the admins on WhatsApp, or try one of these:`,
  actions: [whatsappAction(route, 'Text the admins')],
  suggestions: DEFAULT_SUGGESTIONS
});

const findIntent = (text) => {
  let best = null;
  let bestScore = 0;

  INTENTS.filter((intent) => !intent.weak).forEach((intent) => {
    const score = scoreKeywords(text, intent.keywords);
    if (score > bestScore) {
      best = intent;
      bestScore = score;
    }
  });

  return best || INTENTS.filter((intent) => intent.weak).find(
    (intent) => scoreKeywords(text, intent.keywords) > 0
  );
};

export const getWelcomeMessage = () => ({
  text: `Ndaa/Aa! 👋 Vha khou tuwa afho?\n\nI'm the V2V Assistant. Ask me anything about your trip or student accommodation, or tap a button below.`,
  suggestions: DEFAULT_SUGGESTIONS
});

// context remembers the route and topic, so "and for Gaza?" works as a follow-up
export const getBotReply = (input, context = {}) => {
  const text = clean(input);
  const mentionedRoute = detectRoute(text);
  const route = mentionedRoute || context.route || null;
  const stop = findStop(text);
  const residence = findResidence(text);
  const intent = findIntent(text);
  const nextContext = { route, intentId: intent?.id || context.intentId };

  if (residence) {
    return { ...residenceAnswer(residence), context: nextContext };
  }

  // "how much to Polokwane?" or just "Elim"
  if (stop && (!intent || ['price', 'dropoff', 'routes'].includes(intent.id))) {
    return { ...stopAnswer(stop), context: nextContext };
  }

  if (intent) {
    return { ...intent.answer(route, text), context: nextContext };
  }

  // "and for Gaza?" repeats the last topic for that route
  if (mentionedRoute) {
    const lastIntent = INTENTS.find((item) => item.id === context.intentId);
    const reply = lastIntent ? lastIntent.answer(mentionedRoute, text) : routeOverview(mentionedRoute);
    return { ...reply, context: nextContext };
  }

  return { ...fallback(route), context: nextContext };
};
